import * as _ from 'lodash';
import { FormEvent, useMemo, useState } from 'react';
import { gpsListTypeMap, IGpsItemsForCentralRecords, IGpsItemsForList } from '@nkr/models/index';
import { GpsListType } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent } from 'ag-grid-community';
import { GpsAction } from './gps-action';
import { GpsTrackingNumberType } from './gps-tracking-number-type';

interface IProps {
    gpsCentralRecords: IGpsItemsForCentralRecords[],
    listType: number,
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onSelectAllRows: () => void,
    onRowSelected: (id: number, isSelected: boolean) => void,
    onGridReady: (e: GridReadyEvent) => void,
    onItemSelected: (id: number, action: GpsAction, trackingType: GpsTrackingNumberType, comment: string) => void
}

const GpsCentralGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        if (props.listType != null) {
            e.api.sizeColumnsToFit();
        }
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const getListTypeName = () => {
        let listType = gpsListTypeMap.find(x => x.type == props.listType);

        if (listType != null) {
            return listType.name;
        }

        return '';
    };

    const cols: ColDef[] = [
        {
            colId: 'id',
            field: 'id',
            sortable: false,
            suppressSizeToFit: true,
            hide: true
        },
        {
            colId: 'rowNumber',
            headerName: 'Row #',
            width: 55,
            sortable: false,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Row #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>
        },
        {
            colId: 'targetSurgeryDate',
            field: 'targetSurgeryDate',
            headerName: 'Targeted Surgery Date',
            width: 150,
            sortable: true,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Targeted Surgery Date</SortableHeaderComponent>
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
        },
        {
            colId: 'gpsNumber',
            field: 'gpsNumber',
            headerName: 'GPS #',
            sortable: true,
            width: 55,
            hide: props.isAdmin == false || props.listType == GpsListType.PendingShip,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>GPS #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IGpsItemsForCentralRecords;

                return (
                    <>{data.gpsNumber}</>
                );
            }
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                return <><span>{props.data.donorCenter}</span>  {props.data.rdnCenter != "" ? <span style={{ color: 'red', fontWeight: 'bold' }}> ({props.data.rdnCenter})</span> : ""}</>;
            }
        },

        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
        },
        {
            colId: 'donorTransportMethod',
            field: 'donorTransportMethod',
            headerName: 'Primary Plan',
            sortable: true,
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Primary Plan</SortableHeaderComponent>,
            cellStyle: (params) => {
                return params.value?.toString().toUpperCase().includes('OBC')
                    ? { backgroundColor: 'yellow', fontWeight: 'bold' }
                    : {};
            }
        },
        {
            colId: 'donorTransportMethodBackUp',
            field: 'donorTransportMethodBackUp',
            headerName: 'Backup Plan',
            sortable: true,
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Backup Plan</SortableHeaderComponent>,
            cellStyle: (params) => {
                return params.value?.toString().toUpperCase().includes('OBC')
                    ? { backgroundColor: 'yellow', fontWeight: 'bold' }
                    : {};
            }
        },
        {
            colId: 'gpsStatus',
            field: 'gpsStatus',
            headerName: 'Status',
            sortable: true,
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
        },
        {
            colId: 'returnedOn',
            field: 'returnedOn',
            headerName: 'Returned On',
            sortable: true,
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Returned On</SortableHeaderComponent>,
        },
        {
            colId: 'gpsComments',
            field: 'gpsComments',
            headerName: 'Notes',
            sortable: true,
            width: 200,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Notes</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let label = '';

                let data = params.data as IGpsItemsForList;
                let gpsAction = GpsAction.AddTrackingComment;
                let trackingType = null;

                let isOverridden = false;
                if (data.gpsComments) {
                    label = data.gpsComments;
                    isOverridden = true;
                }

                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            fontStyle: isOverridden ? 'normal' : 'italic',
                            pointerEvents: 'auto',
                            minHeight: '20px'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            //  console.log('Notes clicked!', params.data.id);
                            props.onItemSelected(params.data.id, gpsAction, trackingType, params.data.gpsComments);
                        }}
                    >
                        {label || '[Add Comment]'}
                    </span>

                );
            }
        }
    ];

    const gpsCentralRecords = props.gpsCentralRecords.length > 0 ? props.gpsCentralRecords : (props.isLoading ? null : []);

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => gpsCentralRecords, [gpsCentralRecords]);

    const listTypeName = getListTypeName();

    return (
        <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            headerHeight={40}
            rowHeight={30}

            getContextMenuItems={(params) => {
                return [
                    'copy',
                    'copyWithHeaders',
                    'separator',
                    'export'
                ];
            }}
            overlayNoRowsTemplate={`No [${listTypeName}] gps records...`}
            overlayLoadingTemplate={`Loading [${listTypeName}] gps records...`}
            getRowNodeId={(data: IGpsItemsForCentralRecords) => data.id.toString()}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
            onGridSizeChanged={onGridSizeChanged}
            onRowDataChanged={onRowDataChanged}
            columnHoverHighlight={true}
            onCellClicked={(e: CellClickedEvent) => {
                if (e.colDef.field != 'isSelected' && e.colDef.field != 'actionMenu') {
                }
            }}
            getRowStyle={(params: any) => {
                if (params.data?.isCancelled) {
                    return { backgroundColor: '#c9372e', color: 'white' };
                }
                return {}; // default style
            }}
        />
    );
}

export default GpsCentralGrid;